// apps/web/src/pages/Profile/components/ContactSection.tsx

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Pencil, Phone, Plus, Star, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  useCreateMyContact,
  useDeleteMyContact,
  useMyContacts,
  useUpdateMyContact,
} from '@alamo/api';
import type { ProfileContact, ProfileContactType } from '@alamo/types';
import {
  Badge,
  Button,
  ReasonDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Spinner,
  cn,
} from '@alamo/ui';
import { extractApiError } from '@alamo/utils';

const CONTACT_TYPES: ProfileContactType[] = ['mobile', 'whatsapp', 'phone', 'email'];

const contactSchema = z.object({
  contactType: z.enum(['email', 'phone', 'mobile', 'whatsapp']),
  contactValue: z.string().min(1, 'common:validation.required'),
  isPrimary: z.boolean().optional(),
});

type ContactFormValues = z.infer<typeof contactSchema>;

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  contact?: ProfileContact;
}

function ContactDialog({ open, onClose, contact }: ContactDialogProps) {
  const { t } = useTranslation(['auth', 'common']);
  const create = useCreateMyContact();
  const update = useUpdateMyContact();
  const isEditing = !!contact;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: contact
      ? {
          contactType: contact.contactType,
          contactValue: contact.contactValue,
          isPrimary: contact.isPrimary,
        }
      : {
          contactType: 'mobile',
          contactValue: '',
          isPrimary: false,
        },
  });

  const currentType = watch('contactType');
  const isPending = create.isPending || update.isPending;

  const onSubmit = (values: ContactFormValues) => {
    const payload = {
      contactType: values.contactType,
      contactValue: values.contactValue,
      isPrimary: values.isPrimary ?? false,
    };

    const onSuccess = () => {
      toast.success(
        isEditing
          ? t('auth:profile.contacts.toast.updated')
          : t('auth:profile.contacts.toast.created'),
      );
      reset();
      onClose();
    };

    const onError = (err: unknown) => {
      const { message, code } = extractApiError(err);
      toast.error(
        t(`errors:${code ?? 'generic'}`, {
          defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
        }),
      );
    };

    if (isEditing && contact) {
      update.mutate({ id: contact.id, input: payload }, { onSuccess, onError });
    } else {
      create.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('auth:profile.contacts.edit') : t('auth:profile.contacts.add')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Tipo */}
          <div>
            <label className="block text-sm font-body font-medium text-slate-700 mb-1">
              {t('auth:profile.contacts.fields.type')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {CONTACT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setValue('contactType', type)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm font-body transition-colors min-h-[44px]',
                    currentType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {t(`auth:profile.contacts.type_${type}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Valor */}
          <Input
            label={t('auth:profile.contacts.fields.value')}
            placeholder={
              currentType === 'email'
                ? 'seu@email.com'
                : currentType === 'mobile' || currentType === 'whatsapp'
                  ? '(00) 00000-0000'
                  : '(00) 0000-0000'
            }
            {...register('contactValue')}
            error={errors.contactValue ? t(errors.contactValue.message as string) : undefined}
          />

          {/* Principal */}
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              className="size-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              {...register('isPrimary')}
            />
            <span className="text-sm font-body text-slate-700">
              {t('auth:profile.contacts.fields.is_primary')}
            </span>
          </label>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending}
              aria-busy={isPending || undefined}
            >
              {isPending && <Spinner size="sm" />}
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ContactSection() {
  const { t } = useTranslation(['auth', 'common']);
  const { data: contacts, isLoading } = useMyContacts();
  const deleteContact = useDeleteMyContact();
  const updateContact = useUpdateMyContact();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    contact?: ProfileContact;
  }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<ProfileContact | null>(null);

  const handleSetPrimary = (contact: ProfileContact) => {
    if (contact.isPrimary) return;
    updateContact.mutate(
      { id: contact.id, input: { isPrimary: true } },
      {
        onSuccess: () => toast.success(t('auth:profile.contacts.toast.updated')),
        onError: (err) => {
          const { message } = extractApiError(err);
          toast.error(message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }));
        },
      },
    );
  };

  const handleDelete = ({ reason }: { reason: string }) => {
    if (!deleteTarget) return;
    deleteContact.mutate(
      { id: deleteTarget.id, reason },
      {
        onSuccess: () => {
          toast.success(t('auth:profile.contacts.toast.deleted'));
          setDeleteTarget(null);
        },
        onError: (err) => {
          const { message } = extractApiError(err);
          toast.error(message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }));
          setDeleteTarget(null);
        },
      },
    );
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading font-bold text-secondary-950">
          {t('auth:profile.sections.contacts')}
        </h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setDialogState({ open: true })}
          className="min-h-[44px]"
        >
          <Plus className="size-4" aria-hidden />
          {t('auth:profile.contacts.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : !contacts || contacts.length === 0 ? (
        <p className="text-sm text-slate-500">{t('auth:profile.contacts.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 space-y-0">
          {contacts.map((contact) => (
            <li key={contact.id} className="py-3 flex items-center gap-3">
              <Phone className="size-4 text-slate-400 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-body font-medium text-secondary-950">
                    {contact.contactValue}
                  </span>
                  {contact.isPrimary && (
                    <Badge variant="info" className="text-xs">
                      {t('auth:profile.contacts.primary_badge')}
                    </Badge>
                  )}
                  {contact.isVerified && (
                    <span className="flex items-center gap-1 text-xs text-emerald-700">
                      <CheckCircle className="size-3" aria-hidden />
                      {t('auth:profile.contacts.verified_badge')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {t(`auth:profile.contacts.type_${contact.contactType}`)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!contact.isPrimary && (
                  <button
                    type="button"
                    title={t('auth:profile.contacts.set_primary')}
                    onClick={() => handleSetPrimary(contact)}
                    disabled={updateContact.isPending}
                    className="p-2 rounded-md text-slate-400 hover:text-primary-600 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Star className="size-4" aria-hidden />
                    <span className="sr-only">{t('auth:profile.contacts.set_primary')}</span>
                  </button>
                )}
                <button
                  type="button"
                  title={t('auth:profile.contacts.edit')}
                  onClick={() => setDialogState({ open: true, contact })}
                  className="p-2 rounded-md text-slate-400 hover:text-secondary-700 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Pencil className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.contacts.edit')}</span>
                </button>
                <button
                  type="button"
                  title={t('auth:profile.contacts.delete')}
                  onClick={() => setDeleteTarget(contact)}
                  className="p-2 rounded-md text-slate-400 hover:text-error-600 hover:bg-red-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Trash2 className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.contacts.delete')}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ContactDialog
        // key remonta o dialog por alvo: useForm captura defaultValues na
        // MONTAGEM — sem isso, editar abre com os campos vazios/stale.
        key={dialogState.contact?.id ?? 'new'}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false })}
        contact={dialogState.contact}
      />

      <ReasonDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={t('auth:profile.contacts.delete')}
        description={t('auth:profile.contacts.delete_confirm')}
        reasonLabel={t('common:deletion.reason_label')}
        reasonPlaceholder={t('common:deletion.reason_placeholder')}
        minChars={10}
        maxChars={500}
        confirmLabel={t('common:actions.delete')}
        confirmVariant="danger"
        onSubmit={handleDelete}
        isSubmitting={deleteContact.isPending}
      />
    </section>
  );
}
